import { LitElement, html } from 'lit';
import { store } from '../store.js';
import { getWeekStart, getWeekLabel, getPreviousWeek, getNextWeek, isCurrentWeek } from '../utils/date.js';
import { formatCurrency } from '../utils/format.js';
import { formatQuantity } from '../utils/units.js';
import { showLoading, hideLoading } from '../components/loading-spinner.js';
import { showSuccess, showError } from '../components/toast-notification.js';
import '../components/week-selector.js';

class ShoppingView extends LitElement {
  static properties = {
    _weekStart: { state: true },
  };

  createRenderRoot() { return this; }

  constructor() {
    super();
    this._weekStart = getWeekStart();
  }

  connectedCallback() {
    super.connectedCallback();
    this._onUpdate = () => this.requestUpdate();
    store.on('shoppinglist-updated', this._onUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    store.off('shoppinglist-updated', this._onUpdate);
  }

  _onWeekChange(e) {
    this._weekStart = e.detail.weekStart;
  }

  async _toggleItem(itemName, currentChecked) {
    try {
      await store.updateShoppingItem(this._weekStart, itemName, { checked: !currentChecked });
    } catch (error) {
      console.error('Error updating item:', error);
      showError('Failed to update item');
    }
  }

  async _regenerate() {
    try {
      showLoading();
      await store.generateShoppingList(this._weekStart);
      showSuccess('Shopping list regenerated');
    } catch (error) {
      console.error('Error regenerating shopping list:', error);
      showError(error.message || 'Failed to regenerate shopping list');
    } finally {
      hideLoading();
    }
  }

  render() {
    const items = store.getShoppingListForWeek(this._weekStart);

    const grouped = {};
    items.forEach(item => {
      const cat = item.category || 'other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });

    // Sort unchecked first
    Object.keys(grouped).forEach(cat => {
      grouped[cat].sort((a, b) => {
        if (a.checked === b.checked) return 0;
        return a.checked ? 1 : -1;
      });
    });

    let estimatedTotal = 0;
    items.forEach(item => {
      if (item.estimated_price) estimatedTotal += parseFloat(item.estimated_price);
    });

    return html`
      <div class="max-w-7xl mx-auto px-4 py-6 md:ml-64">
        <div class="no-print flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <h1 class="text-3xl font-bold text-gray-900 mb-4 sm:mb-0">Shopping List</h1>
          <div class="flex gap-2">
            <button @click=${() => window.print()} class="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">Print List</button>
            <button @click=${this._regenerate} class="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">Regenerate</button>
          </div>
        </div>

        <week-selector week-start=${this._weekStart} @week-change=${this._onWeekChange}></week-selector>

        <div class="mt-6">
          ${items.length === 0 ? html`
            <div class="no-print bg-white rounded-lg shadow p-12 text-center">
              <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
              <p class="text-gray-500 mb-4">No shopping list for this week</p>
              <a href="#/planner" class="text-blue-600 hover:underline">Plan meals and generate list</a>
            </div>
          ` : html`
            <div class="print-only">
              <h1 class="print-title">Shopping List</h1>
              <p class="print-subtitle">${getWeekLabel(this._weekStart)}</p>
            </div>

            <div class="space-y-6">
              ${Object.keys(grouped).sort().map(category => html`
                <div class="bg-white rounded-lg shadow print-section">
                  <div class="px-6 py-4 border-b border-gray-200">
                    <h2 class="text-lg font-semibold text-gray-900 capitalize">${category}</h2>
                  </div>
                  <div class="divide-y divide-gray-200">
                    ${grouped[category].map(item => {
                      const isChecked = item.checked === true;
                      return html`
                        <div class="p-4 flex items-center gap-4 print-item">
                          <input type="checkbox" ?checked=${isChecked} @change=${() => this._toggleItem(item.item, isChecked)} class="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 no-print">
                          <div class="print-checkbox"></div>
                          <div class="flex-1 ${isChecked ? 'opacity-50' : ''}">
                            <p class="font-medium text-gray-900 ${isChecked ? 'line-through' : ''}">${item.item}</p>
                            <p class="text-sm text-gray-600">${formatQuantity(item.quantity)} ${item.unit}</p>
                          </div>
                          ${item.estimated_price ? html`
                            <div class="text-right ${isChecked ? 'opacity-50' : ''}">
                              <p class="font-medium text-gray-900">${formatCurrency(item.estimated_price)}</p>
                            </div>
                          ` : ''}
                        </div>
                      `;
                    })}
                  </div>
                </div>
              `)}
            </div>

            <div class="bg-white rounded-lg shadow mt-6 print-section">
              <div class="px-6 py-4 flex items-center justify-between">
                <span class="text-lg font-semibold text-gray-900">Estimated Total</span>
                <span class="text-2xl font-bold text-gray-900">${formatCurrency(estimatedTotal)}</span>
              </div>
            </div>
          `}
        </div>
      </div>
    `;
  }
}

customElements.define('shopping-view', ShoppingView);
